import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  Select,
  Upload,
  Typography,
  message,
  UploadFile,
  UploadProps,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlusOutlined, UploadOutlined, MinusCircleOutlined } from "@ant-design/icons";

import TiptapEditor from "../utils/TiptapEditor";

import ContentSection from "../../../../components/layout/mainContent/ContentSection";
import MainContentFooter from "../../../../components/layout/mainContent/MainContentFooter";
import Spinner from "../../../../components/layout/spinner/Spinner";
import { Colors } from "../../../../constants/Colors";

import { useAddGoGoLiveParentGuideMutation } from "../../../../graphql/operations/add-parent-guide";
import { useGetGoGoLiveOptionListQuery } from "../../../../graphql/operations/get-gogolive-option-list";
import { useUpdateGoGoLiveParentGuideMutation } from "../../../../graphql/operations/update-parent-guide";
import { useUploadFileMutation } from "../../../../graphql/operations/upload-file";
import { AfBlobPath } from "../../../../graphql/operations/@types";

import requiredRule from "../../../../utils/formRules/requiredRule";
import imageValidation from "../../../../utils/formValidation/imageValidation";
import { renderAntDMessageConfig } from "../../../../utils/renderAntDMessageConfig";

interface GoGoLiveDetailTabENProps {
  englishForm: any;
  market: string;
  canEdit: boolean;
  isView: boolean;
  userName: string | null | undefined;
}

const GoGoLiveDetailTabEN = ({
  englishForm,
  market,
  canEdit,
  isView,
  userName,
}: GoGoLiveDetailTabENProps) => {
  const navigate = useNavigate();
  const { id }: any = useParams();
  const [uploadFile] = useUploadFileMutation();
  const [addParentGuide, { loading: addLoading }] = useAddGoGoLiveParentGuideMutation();
  const [updateParentGuide, { loading: updateLoading }] = useUpdateGoGoLiveParentGuideMutation();
  const { data: optionData, loading: optionLoading, error: optionError } = useGetGoGoLiveOptionListQuery();

  const settingsForm = englishForm;
  const [uploadedFile, setUploadedFile] = useState<UploadFile[]>([]);
  const [uploadedFileName, setUploadedFileName] = useState<any>();
  const [PdfFile, setPdfFile] = useState<UploadFile[]>([]);
  const [PdfFileName, setPdfFileName] = useState<string | undefined | null>();
  const pdfFileList = Form.useWatch("printableSheet", settingsForm);

  const filterOption: any = (
    input: string,
    option: { label: string; value: string }
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const stripHTML = (html: string): string => {
    return html.replace(/<[^>]+>/g, "").replace(/\s+/g, "");
  };

  const selectedOptionRenderer = (option: any) => {
    return (
      <div className="flex items-center gap-2 mt-[1px]">
        <p>{option.toyName}</p>
      </div>
    );
  };

  const customOptionRenderer = (option: any) => {
    return (
      <div className="flex items-center gap-4">
        <img
          src={option.toyImageUrl}
          alt={option.toyImageFile}
          className="w-16 h-16"
        />
        <p>{option.toyName}</p>
      </div>
    );
  };

  const props: UploadProps = {
    beforeUpload: imageValidation(100, "KB"),
    disabled: !canEdit,
    maxCount: 1,
    onChange: (info) => {
      console.log(info);
      if (info.fileList.length === 0) {
        setUploadedFileName(undefined);
        setUploadedFile([]);
        return;
      }
      if (info.file.status === "done") {
        setUploadedFileName(info.file.name);
        setUploadedFile(info.fileList)
      }
    },
    onRemove: () => {
      setUploadedFileName(undefined);
      setUploadedFile([]);
    }
  };

  const pdfProps: UploadProps = {
    action: "https://httpbin.org/post",
    disabled: !canEdit,
    maxCount: 1,
    name: "file",
    headers: {
      authorization: "authorization-text",
    },
    beforeUpload: (file) => {
      const isPdf = file.type.split("/")[1] === "pdf";
      if (!isPdf) {
        message.error(renderAntDMessageConfig("Only upload pdf file!"));
      }

      const isSizeValid = file.size <= 1024 * 1024;
      if (!isSizeValid) {
        message.error(renderAntDMessageConfig("Please upload an file that is less than 1MB."));
      }

      return (isPdf && isSizeValid) || Upload.LIST_IGNORE;
    },
    onChange: (info) => {
      console.log(info);
      if (info.fileList.length === 0) {
        setPdfFileName(undefined);
        setPdfFile([]);
        return;
      }
      setPdfFileName(info.file.name);
      setPdfFile(info.fileList);
    },
    onRemove: () => {
      setPdfFileName(undefined);
      setPdfFile([]);;
    }
  };

  const handleSubmit = (values: any) => {
    const formData = {
      ...values,
      yearAndMonth: dayjs(values.yearAndMonth).format("YYYY-MM-01"),
      sessions: (values.sessions as number[] ?? []).map((id) => ({ sessionId: id })),
      previewVideoCoverImageFileName: uploadedFileName ? uploadedFileName : values.previewVideoCoverImageFileName[0].name,
      songList: (values.songList as { discCode: string; eTabCode: string; songs: number[] }[]).map((item) => ({
        ...item,
        songs: item.songs.map((id) => ({ songId: id })),
      })),
      wordList: (values.wordList as number[]).map((id) => ({ talkAlongCardId: id })),
      toys: (values.toys as number[] ?? []).map((id) => ({ toyId: id })),
      printableSheet: PdfFileName ?? values.printableSheet?.[0]?.name ?? "",
      user: userName,
      isNotifyTeams: values.isNotifyTeams ? true : false
    }
    if (uploadedFile.length > 0) {
      uploadFile({
        variables: {
          blobs: [
            {
              file: uploadedFile[0].originFileObj,
              path: AfBlobPath.GoGoLiveParentGuideImagesCover,
            },
          ],
        },
      }).catch((e) => console.log(e));
    }
    if (PdfFile.length > 0) {
      uploadFile({
        variables: {
          blobs: [
            {
              file: PdfFile[0].originFileObj,
              path: AfBlobPath.GoGoLiveParentGuidePdfs,
            },
          ],
        },
      }).catch((e) => console.log(e));
    }

    if (id) {
      updateParentGuide({
        variables: {
          ...formData,
        },
      }).then((res) => {
        const error = res?.data?.updateGoGoLiveParentGuide?.error;
        if (error) {
          message.error(error);
        } else {
          message.success(
            "Go, Go Live! details have been successfully submitted."
          );
          navigate("/club-app/ves");
        }
      })
        .catch((e) => console.log(e));
    } else {
      addParentGuide({
        variables: {
          ...formData,
        },
      }).then((res) => {
        const error = res?.data?.addGoGoLiveParentGuide?.error;
        if (error) {
          message.error(error);
        } else {
          message.success(
            "Go, Go Live! details have been successfully submitted."
          );
          navigate("/club-app/ves");
        }
      })
        .catch((e) => console.log(e));
    }
  };

  if (optionError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (optionLoading || addLoading || updateLoading) {
    return <Spinner />;
  }

  return (
    <ContentSection title="" showTitle={false}>
      <Form
        form={settingsForm}
        name="settings"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 12 }}
        onFinish={handleSubmit}
        initialValues={{
          learningGoals: [''],
          songList: [{}],
        }}
      >
        <Form.Item name="goGoLiveParentGuideId" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          label="Year & month"
          name="yearAndMonth"
          rules={[requiredRule]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MMM"
            picker="month"
            disabledDate={(current) => {
              return (
                current && current < dayjs().endOf("month").subtract(1, "month")
              );
            }}
            disabled={!canEdit || !(canEdit && id === undefined)}
            placeholder="Select a month"
          />
        </Form.Item>
        <Form.Item
          label="CAP level & session"
          required
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <Form.Item
              name="levelId"
              style={{ width: "50%" }}
              rules={[requiredRule]}
            >
              <Select
                options={optionData?.goGoLiveLevelList.map((item) => ({
                  label: item.levelName,
                  value: item.levelId,
                }))}
                disabled={!canEdit || !(canEdit && id === undefined)}
                placeholder="Select a CAP level"
              />
            </Form.Item>
            <Form.Item
              name="sessions"
              style={{ width: "50%" }}
              rules={[requiredRule]}
            >
              <Select
                mode="multiple"
                options={optionData?.goGoLiveSessionList.map((item) => ({
                  label: item.sessionName,
                  value: item.sessionId,
                }))}
                disabled={!canEdit}
                placeholder="Select session (s)"
                filterOption={filterOption}
              />
            </Form.Item>
          </div>
        </Form.Item>
        <div
          className="parent-container"
          style={{ backgroundColor: Colors.lightWhite }}
        >
          <h5>Parent Guide Main Content</h5>
          <Form.Item
            label="Lesson title"
            name="lessonTitle"
            rules={[requiredRule]}
          >
            <Input disabled={!canEdit} />
          </Form.Item>
          <Form.Item
            label="Preview video"
            name="previewVideoVimeoId"
            extra={"Enter the ID of the video uploaded on Vimeo"}
          >
            <Input
              disabled={!canEdit}
              placeholder="Vimeo ID" />
          </Form.Item>
          <Form.Item
            label="Preview video cover image"
            name="previewVideoCoverImageFileName"
            rules={[requiredRule]}
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload
              {...props}
              action="https://httpbin.org/post"
              listType="picture-card"
            >
              {canEdit ? (
                <div style={{ position: "relative" }}>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                  <ul className="image-ul">
                    <li>Max file size: 100KB</li>
                    <li>Recommended width: 668 x 376x (16:9)</li>
                  </ul>
                </div>
              ) : null}

            </Upload>
          </Form.Item>
          <Form.Item
            label="Introduction"
            name="introduction"
            rules={[requiredRule]}
          >
            <Input.TextArea
              disabled={!canEdit}
              placeholder="Autosize height based on content lines" />
          </Form.Item>
          <Form.Item
            label="Learning goals"
            required
          >
            <Form.List name="learningGoals"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(new Error("This field is required!"));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>{fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, height: 32 }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ width: "100%" }}
                      rules={[requiredRule]}
                    >
                      <Input disabled={!canEdit} />
                    </Form.Item>
                    {canEdit && (
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'rgba(0, 0, 0, 0.45)', }} />
                    )}
                  </div>
                ))}
                  {errors.length > 0 && (
                    <Form.Item>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  )}
                  {canEdit && (
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Row
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            label="Learning points"
            required
          >
            <div className="tiptap-wrapper">
              <TiptapEditor
                value={settingsForm.getFieldValue("languagePoint")}
                onChange={(html) => settingsForm.setFieldsValue({ languagePoint: html })}
                canEdit={canEdit}
              />
              <Form.Item
                name="languagePoint"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      const hasMeaningfulContent =
                        !!value &&
                        /<[^>]+>(.*?)<\/[^>]+>/.test(value) &&
                        stripHTML(value).trim().length > 0;

                      return hasMeaningfulContent
                        ? Promise.resolve()
                        : Promise.reject(new Error("This field is required!"));
                    },
                  },
                ]}
                valuePropName="value"
                getValueFromEvent={(v) => v}
                noStyle
              >
                <Input hidden />
              </Form.Item>
            </div>
            <Typography>
              <ul className="custom-ul">
                <li>
                  Use <b>B</b> to highlight text (e.g. buy)
                </li>
                <li>Use underscores _____ to create blanks</li>
                <li>Press Shift+Enter to break a line within the same paragraph</li>
                <li>Press Enter to start a new paragraph</li>
              </ul>
            </Typography>
          </Form.Item>
          <Divider />
          <Form.Item
            label="Book pages"
            name="bookPage"
          >
            <Input
              disabled={!canEdit}
              placeholder="Book #, pages #-#" />
          </Form.Item>
          <Form.Item
            label="Song list"
            required
          >
            <Form.List name="songList"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(new Error("This field is required!"));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>{fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'discCode']}
                      style={{ width: "25%", marginBottom: 0 }}
                      rules={[requiredRule]}
                    >
                      <Input
                        disabled={!canEdit}
                        placeholder="Disc code" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'eTabCode']}
                      style={{ width: "25%", marginBottom: 0 }}
                      rules={[requiredRule]}
                    >
                      <Input
                        disabled={!canEdit}
                        placeholder="E-Tab code" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'songs']}
                      style={{ width: "50%", marginBottom: 0 }}
                      rules={[requiredRule]}
                    >
                      <Select
                        mode="multiple"
                        options={optionData?.goGoLiveSongList.map((item) => ({
                          label: item.songName,
                          value: item.songId,
                        }))}
                        disabled={!canEdit}
                        placeholder="Select song title(s)"
                        filterOption={filterOption}
                      />
                    </Form.Item>
                    {canEdit && (
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'rgba(0, 0, 0, 0.45)', }} />
                    )}
                  </div>
                ))}
                  {errors.length > 0 && (
                    <Form.Item>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  )}
                  {canEdit && (
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Row
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            label="Word list"
            name="wordList"
            rules={[requiredRule]}
          >
            <Select
              mode="multiple"
              options={optionData?.goGoLiveTalkAlongCardList.map((item) => ({
                label: `${item.cardNumber} ${item.word}`,
                value: item.talkAlongCardId,
                optionLabel: item.cardNumber,
              }))}
              optionLabelProp="optionLabel"
              disabled={!canEdit}
              placeholder="Select TAC number(s)"
              filterOption={filterOption}
            />
          </Form.Item>
          <Divider />
          <Form.Item
            label={<span>Play Along! toys <span className="text-primary">(optional)</span></span>}
            name="toys"
          >
            <Select
              mode="multiple"
              optionLabelProp="custom"
              dropdownRender={(menu) => <div>{menu}</div>}
              disabled={!canEdit}
              placeholder="Select toy(s)"
              filterOption={filterOption}
            >
              {optionData?.goGoLiveToyList.map((item) => (
                <Select.Option
                  key={item.toyId}
                  value={item.toyId}
                  label={item.toyName}
                  custom={selectedOptionRenderer(item)}
                  children={customOptionRenderer(item)}
                />
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={<span>Show and tell <span className="text-primary">(optional)</span></span>}
            name="showAndTell"
          >
            <Input.TextArea
              disabled={!canEdit}
              placeholder="Autosize height based on content lines=" />
          </Form.Item>
          <Form.Item
            label={<span>Printable sheet <span className="text-primary">(optional)</span></span>}
            name="printableSheet"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            {canEdit ? (
              <Upload
                {...pdfProps}
              >
                <Button icon={<UploadOutlined />} >
                  Click to Upload
                </Button>
                {(!pdfFileList || pdfFileList.length === 0) && (
                  <p className="text-secondary" >
                    Max file size: 1MB
                  </p>
                )
                }
              </Upload>
            ) : (
              <Upload
                {...pdfProps}
              >
                {(!pdfFileList || pdfFileList.length === 0) && (
                  <p className="text-secondary">
                    Max file size: 1MB
                  </p>
                )
                }
              </Upload>
            )}
          </Form.Item>
          <Form.Item
            label="Teams notification"
            name="isNotifyTeams"
            valuePropName="checked"
          >
            <Checkbox
              disabled={!canEdit}
              >
              Notify each market's staff via Teams for review or translation
          </Checkbox>
          </Form.Item>
        </div>

        {!isView && (
          <MainContentFooter
            isLoading={addLoading || updateLoading}
            disabled={!canEdit}
            backTo="/club-app/ves"
          />
        )}
      </Form>
    </ContentSection>
  );
};

export default GoGoLiveDetailTabEN;
